import type { WorldDocument } from "../document/types.ts";
import { isOpId } from "./op-read.ts";
import { LIVE_OPS_VERSION, type LiveOp } from "./op-types.ts";
import { encodeOp } from "./ops.ts";
import type { FrozenReason, ResumePoint } from "./protocol-stream.ts";
import type { LiveServerMessage } from "./protocol.ts";
import { resolveOp } from "./resolve.ts";
import { claimsMessage, claimsWorked } from "./room-claims.ts";
import { CONTRIBUTORS_MAX, KEY_TAGS_MAX, RING_BYTES, RING_OPS, ROOM_TAGS_MAX, type Contributor, type RingEntry, type RoomEntry, type RoomFlush, type RoomSender, type RoomState, type RoomStep, type TagCounter } from "./room-types.ts";

/**
 * A world's room as a pure reducer (ADR-086 decisions 21, 22 and 28): each step takes the room and one message
 * and returns the room after it with what to send to whom, and writes into nothing it was given. The host
 * (the build server) owns the sockets, the clock, the journal and the store: it calls a step for each frame in
 * arrival order, journals the entry a sequenced operation returns before it sends anything, and passes the
 * time in. Two rooms given the same messages in the same order hold the same state.
 *
 * An operation is sequenced when at least one of its intents applies: it takes the next sequence, goes to
 * every other tab as `op` and is answered `ack` with that sequence. One that changes nothing is answered `ack`
 * with no sequence and recorded nowhere. An operation whose id is at or below the highest counter the room
 * sequenced for its sender's key and tab tag is never weighed again: an operation applied twice may differ
 * from once, so this is what makes a resend safe, and the counters outlive the ring. A counter may be dropped
 * for room (a sender keeps `KEY_TAGS_MAX`, the room `ROOM_TAGS_MAX`); an id the ring still holds under its
 * sender's key is refused all the same.
 */

/**
 * What a room opens on: the head's document and revision, the epoch the host drew for this opening, and the
 * counters a journal carried over, none for a world never edited live.
 */
export interface RoomOpening {
  readonly document: WorldDocument;
  readonly epoch: string;
  readonly revision: number;
  readonly counters?: readonly TagCounter[];
}

/**
 * Opens a room at sequence 0.
 *
 * @param opening The head, the epoch and the counters carried over.
 * @returns The room: nothing in its ring, nothing unflushed, no claim.
 */
export function openRoom(opening: RoomOpening): RoomState {
  return { document: opening.document, epoch: opening.epoch, seq: 0, revision: opening.revision, flushed: 0, ring: [], ringBytes: 0, counters: opening.counters ?? [], unflushedSince: null, contributors: [], flushing: null, claims: [], frozen: null };
}

/**
 * The lowest sequence a tab can resume from: the one before the oldest operation the ring holds, or the
 * room's own when the ring is empty.
 *
 * @param state The room.
 * @returns The floor.
 */
export function roomFloor(state: RoomState): number {
  return state.ring[0] === undefined ? state.seq : state.ring[0].seq - 1;
}

/**
 * How many bytes a text takes as UTF-8, counted without a platform encoder.
 *
 * @param text The text.
 * @returns The bytes; a surrogate without its pair counts three, as an encoder writes it.
 */
function utf8Length(text: string): number {
  let bytes = 0;
  for (let i = 0; i < text.length; i += 1) {
    const unit = text.charCodeAt(i);
    if (unit < 0x80) {
      bytes += 1;
    } else if (unit < 0x800) {
      bytes += 2;
    } else if (unit >= 0xd800 && unit <= 0xdbff && text.charCodeAt(i + 1) >= 0xdc00 && text.charCodeAt(i + 1) <= 0xdfff) {
      bytes += 4;
      i += 1;
    } else {
      bytes += 3;
    }
  }
  return bytes;
}

/**
 * A step that refuses its message and leaves the room as it is.
 *
 * @param state The room.
 * @param refused Why.
 * @param message What to tell the sender, if anything.
 * @returns The step.
 */
function refusing(state: RoomState, refused: RoomStep["refused"], message?: LiveServerMessage): RoomStep {
  return { state, send: message === undefined ? [] : [{ to: "sender", message }], refused, entry: null };
}

/**
 * The highest counter the ring holds for a sender's key and tab tag.
 *
 * @param ring The ring.
 * @param key The sender's key.
 * @param tag The tab tag.
 * @returns The counter, 0 when the ring holds no operation of that key and tag.
 */
function ringCounter(ring: readonly RingEntry[], key: string, tag: string): number {
  let highest = 0;
  for (const held of ring) {
    const dash = held.op.id.lastIndexOf("-");
    if (held.key === key && held.op.id.slice(0, dash) === tag) {
      highest = Math.max(highest, Number(held.op.id.slice(dash + 1)));
    }
  }
  return highest;
}

/**
 * The place of the counter longest without an operation among those a test picks.
 *
 * @param counters The counters.
 * @param among Which counters to look at.
 * @returns Its index, the first of several of one time.
 */
function oldest(counters: readonly TagCounter[], among: (held: TagCounter) => boolean): number {
  let found = -1;
  counters.forEach((held, i) => {
    found = among(held) && (found < 0 || held.at < (counters[found] as TagCounter).at) ? i : found;
  });
  return found;
}

/**
 * The counters after an operation was sequenced: its sender's counter for its tag raised. A sender past
 * `KEY_TAGS_MAX` tags loses its own counter longest without an operation, and a room past `ROOM_TAGS_MAX`
 * the one longest without an operation of all.
 *
 * @param counters The counters.
 * @param key The sender's key.
 * @param tag The operation's tab tag.
 * @param n The operation's counter.
 * @param at The time.
 * @returns The counters.
 */
function counted(counters: readonly TagCounter[], key: string, tag: string, n: number, at: number): readonly TagCounter[] {
  const raised: TagCounter = { key, tag, n, at };
  if (counters.some((held) => held.key === key && held.tag === tag)) {
    return counters.map((held) => (held.key === key && held.tag === tag ? raised : held));
  }
  const own = counters.filter((held) => held.key === key).length;
  const dropped = own >= KEY_TAGS_MAX ? oldest(counters, (held) => held.key === key) : counters.length >= ROOM_TAGS_MAX ? oldest(counters, () => true) : -1;
  return [...counters.filter((_, i) => i !== dropped), raised];
}

/**
 * The contributors after operations of one sender's key: its count raised and its place the last, and past
 * `CONTRIBUTORS_MAX` the contributor longest without an operation dropped.
 *
 * @param contributors The contributors, the last one last.
 * @param key The sender's key.
 * @param ops How many operations to add.
 * @returns The contributors.
 */
function contributed(contributors: readonly Contributor[], key: string, ops: number): readonly Contributor[] {
  const held = contributors.find((contributor) => contributor.key === key)?.ops ?? 0;
  const next = [...contributors.filter((contributor) => contributor.key !== key), { key, ops: held + ops }];
  return next.length > CONTRIBUTORS_MAX ? next.slice(next.length - CONTRIBUTORS_MAX) : next;
}

/**
 * The ring with an entry added, the oldest dropped until it is within `RING_OPS` and `RING_BYTES`.
 *
 * @param state The room.
 * @param entry The entry.
 * @returns The ring and the bytes it takes.
 */
function ringWith(state: RoomState, entry: RoomEntry): Pick<RoomState, "ring" | "ringBytes"> {
  const added: RingEntry = { ...entry, bytes: utf8Length(encodeOp(entry.op)) };
  let ringBytes = state.ringBytes + added.bytes;
  let dropped = 0;
  while (state.ring.length + 1 - dropped > RING_OPS || (ringBytes > RING_BYTES && dropped < state.ring.length)) {
    ringBytes -= (state.ring[dropped] as RingEntry).bytes;
    dropped += 1;
  }
  return { ring: [...state.ring.slice(dropped), added], ringBytes };
}

/**
 * Takes an operation from a sender.
 *
 * @param state The room; never written into.
 * @param sender Who sent it: the socket's id and the key its counters and contributions are kept under.
 * @param op The operation, as `opOf` read it; never written into.
 * @param now The time.
 * @returns When an intent applied: the room at the next sequence with the document `resolveOp` leaves, `op`
 *   for the others and `ack` with the sequence for the sender, and the entry to journal. When none applied:
 *   the room as given and `ack` with no sequence. Refused, the room as given: `invalid` for an id that is not
 *   an operation's, sent nothing; `frozen`, answered `nack`; `resent` for an id at or below its sender's
 *   counter for the tag, or, when that counter was dropped, at or below the highest the ring holds for it,
 *   answered `ack` with the sequence it took when the ring still holds it under that key and with none
 *   otherwise.
 */
export function acceptOp(state: RoomState, sender: RoomSender, op: LiveOp, now: number): RoomStep {
  if (!isOpId(op.id)) {
    return refusing(state, "invalid");
  }
  if (state.frozen !== null) {
    return refusing(state, "frozen", { t: "nack", id: op.id, reason: "frozen" });
  }
  const dash = op.id.lastIndexOf("-");
  const tag = op.id.slice(0, dash);
  const n = Number(op.id.slice(dash + 1));
  const counter = state.counters.find((held) => held.key === sender.key && held.tag === tag);
  if (n <= (counter === undefined ? ringCounter(state.ring, sender.key, tag) : counter.n)) {
    return refusing(state, "resent", { t: "ack", id: op.id, seq: state.ring.find((held) => held.op.id === op.id && held.key === sender.key)?.seq ?? null });
  }
  const resolved = resolveOp(state.document, op);
  const applied = op.list.filter((_, i) => resolved.outcomes[i]?.status === "applied");
  if (applied.length === 0) {
    return { state, send: [{ to: "sender", message: { t: "ack", id: op.id, seq: null } }], refused: null, entry: null, outcomes: resolved.outcomes };
  }
  const seq = state.seq + 1;
  const entry: RoomEntry = { seq, at: now, by: sender.id, key: sender.key, op };
  const next: RoomState = {
    ...state,
    document: resolved.document,
    seq,
    ...ringWith(state, entry),
    counters: counted(state.counters, sender.key, tag, n, now),
    unflushedSince: state.unflushedSince ?? now,
    contributors: contributed(state.contributors, sender.key, 1),
    claims: claimsWorked(state.claims, sender.id, state.document, applied, now),
  };
  return { state: next, send: [{ to: "others", message: { t: "op", seq, by: sender.id, ...op } }, { to: "sender", message: { t: "ack", id: op.id, seq } }], refused: null, entry, outcomes: resolved.outcomes };
}

/**
 * Applies a journaled operation to a room that is being rebuilt: what `acceptOp` did when it was first taken,
 * with nothing sent.
 *
 * @param state The room; never written into.
 * @param entry The journal's line.
 * @returns The room after it, or null when the line does not follow the room's sequence or the room would
 *   not sequence its operation again (its id is already counted, or it changes nothing): a journal that does
 *   not belong to this room.
 */
export function replayEntry(state: RoomState, entry: RoomEntry): RoomState | null {
  if (entry.seq !== state.seq + 1) {
    return null;
  }
  const step = acceptOp(state, { id: entry.by, name: "", key: entry.key }, entry.op, entry.at);
  return step.entry === null ? null : step.state;
}

/**
 * What a socket is told when it joins the stream: the room as it stands, then the claims.
 *
 * @param state The room.
 * @returns `stream` and `claims` for the sender.
 */
export function greetTab(state: RoomState): RoomStep {
  const stream: LiveServerMessage = { t: "stream", v: LIVE_OPS_VERSION, epoch: state.epoch, seq: state.seq, floor: roomFloor(state), revision: state.revision };
  return { state, send: [{ to: "sender", message: stream }, { to: "sender", message: claimsMessage(state.claims) }], refused: null, entry: null };
}

/**
 * Answers a tab that says what it holds.
 *
 * @param state The room.
 * @param point The tab's version, epoch and sequence.
 * @returns For this room's epoch and a sequence from the floor to the room's own: the operations after it, in
 *   order, for the sender, each as it was sent on. For anything else `resync`. For another protocol version
 *   nothing, refused as `version`.
 */
export function resumeTab(state: RoomState, point: ResumePoint): RoomStep {
  if (point.v !== LIVE_OPS_VERSION) {
    return refusing(state, "version");
  }
  if (point.epoch !== state.epoch || point.seq < roomFloor(state) || point.seq > state.seq) {
    return { state, send: [{ to: "sender", message: { t: "resync" } }], refused: null, entry: null };
  }
  const tail = state.ring.filter((held) => held.seq > point.seq);
  return { state, send: tail.map((held) => ({ to: "sender", message: { t: "op", seq: held.seq, by: held.by, ...held.op } })), refused: null, entry: null };
}

/**
 * Freezes a room: it takes no operation until it is opened again.
 *
 * @param state The room; never written into.
 * @param reason Why.
 * @returns The frozen room and `frozen` for every tab; a room already frozen is returned as given and keeps
 *   its first reason.
 */
export function freezeRoom(state: RoomState, reason: FrozenReason): RoomStep {
  return state.frozen !== null ? refusing(state, null) : { state: { ...state, frozen: reason }, send: [{ to: "all", message: { t: "frozen", reason } }], refused: null, entry: null };
}

/**
 * Starts a flush of what is unflushed.
 *
 * @param state The room; never written into.
 * @returns What to write and the room with that flush running, its tallies started again for what arrives
 *   meanwhile; or no flush and the room as given when nothing is unflushed, a flush is running or the room is
 *   frozen.
 */
export function startFlush(state: RoomState): { readonly state: RoomState; readonly flush: RoomFlush | null } {
  if (state.unflushedSince === null || state.flushing !== null || state.frozen !== null) {
    return { state, flush: null };
  }
  const flushing = { seq: state.seq, since: state.unflushedSince, contributors: state.contributors };
  return { state: { ...state, flushing, unflushedSince: null, contributors: [] }, flush: { ...flushing, from: state.flushed + 1, document: state.document, counters: state.counters } };
}

/**
 * Ends the flush that is running.
 *
 * @param state The room; never written into.
 * @param revision The head's revision once the flush landed, or null when it failed and may be tried again.
 * @returns The room: landed, on that revision with the flushed sequence marked; failed, with what the flush
 *   held put back under what arrived meanwhile. A room with no flush running is returned as given.
 */
export function endFlush(state: RoomState, revision: number | null): RoomState {
  const { flushing } = state;
  if (flushing === null) {
    return state;
  }
  if (revision !== null) {
    return { ...state, revision, flushed: flushing.seq, flushing: null };
  }
  const contributors = state.contributors.reduce((merged, later) => contributed(merged, later.key, later.ops), flushing.contributors);
  return { ...state, flushing: null, unflushedSince: flushing.since, contributors };
}
